import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import type { ExtractedEvent, EventReviewWarning } from '../types';
import { api, isAuthPending } from './api';
import { clearSwrCacheByPrefix } from './swrCache';
import { eventDayKey, isAllDayEvent } from './eventDates';
import { instantToEventDateTime, type EventRangeFormatOptions } from './eventTemporal';
import type {
  DateProofCandidate, DateProofCurrentEvent, DateProofDetail, DateProofFields,
  DateProofHistoryEntry, DateProofPanelStatus,
} from './dateProofTypes';
import EventTemporalWarning from '../components/EventTemporalWarning';
import DateProofPanel from '../components/DateProofPanel';

/**
 * Transport boundary for the coordinator's server-ledger adapter. UI DTOs remain
 * in dateProofTypes; candidate digests and concurrency tokens are transport-only.
 * No proposed values, actor identity or client-created proof are sent on choose.
 */
export interface DateProofReadResponse {
  current: DateProofCurrentEvent;
  details: DateProofDetail | null;
  candidates: readonly (DateProofCandidate & { evidenceDigest: string })[];
  history: readonly DateProofHistoryEntry[];
  expectedEtag: string;
}

export interface DateProofChooseResponse {
  /** Acknowledges the durable application, not merely acceptance of a job. */
  receiptId: string;
  event: ExtractedEvent;
  proof: DateProofReadResponse;
}

export interface DateProofVerifyResponse {
  /** Existing durable Jobs identity; there is deliberately no browser polling. */
  jobPk: string;
  jobId: string;
}

/** One replaceable adapter boundary; refuse unmapped or unversioned alternatives. */
export function readDateProofResponse(proof: DateProofReadResponse): DateProofReadResponse {
  if (!proof?.current || !Array.isArray(proof.candidates) || !Array.isArray(proof.history)
    || !proof.expectedEtag?.trim() || !('details' in proof)
    || proof.details != null && (!proof.details.date || !proof.details.time || !proof.details.coverage)
    || new Set(proof.candidates.map((candidate) => candidate.id)).size !== proof.candidates.length
    || proof.candidates.some((candidate) => !candidate.id?.trim() || !candidate.values || !candidate.coverage
      || !Array.isArray(candidate.allowedFields)
      || candidate.allowedFields.some((field: unknown) => !['date', 'time', 'both'].includes(String(field)))
      || candidate.allowedFields.length > 0 && !candidate.evidenceDigest?.trim())) {
    throw new Error('The stored proof response needs a server DTO adapter.');
  }
  return proof;
}

export function dateProofPath(event: Pick<ExtractedEvent, 'rowKey'>): string {
  return `/api/mgmt/events/${encodeURIComponent(event.rowKey)}/date-proof`;
}

export function dateProofChooseBody(
  event: Pick<ExtractedEvent, 'partitionKey'>,
  proof: DateProofReadResponse,
  candidateId: string,
  fields: DateProofFields,
) {
  const candidate = proof.candidates.find((item) => item.id === candidateId);
  if (!proof.expectedEtag?.trim() || !candidate?.evidenceDigest?.trim()
    || !candidate.allowedFields.includes(fields)) {
    throw new Error('This choice has no current server-admitted evidence. Refresh stored evidence.');
  }
  return {
    partitionKey: event.partitionKey, expectedEtag: proof.expectedEtag,
    candidateId, evidenceDigest: candidate.evidenceDigest, fields,
  };
}

/** Preserve unrelated warnings; never confuse source coverage with date proof. */
export function nonTemporalWarnings(warnings?: readonly EventReviewWarning[]): EventReviewWarning[] {
  return warnings?.filter((warning) => warning !== 'check-date' && warning !== 'time-unverified') ?? [];
}

/**
 * A stored all-day end is the LAST day for every source (#70); iCal's exclusive DTEND is made inclusive when
 * the feed is ingested (#55). Only a caller that knows an end is exclusive says so.
 */
export function dateProofRangeOptions(_event: ExtractedEvent, options?: EventRangeFormatOptions): EventRangeFormatOptions {
  return { ...options, allDayEndExclusive: options?.allDayEndExclusive ?? false };
}

/** Loading fallback only, derived from the already-saved event, never a candidate. */
export function currentDateProofValues(event: ExtractedEvent): DateProofCurrentEvent {
  const allDay = isAllDayEvent(event);
  const start = instantToEventDateTime(event.startUtc);
  const end = event.endUtc ? instantToEventDateTime(event.endUtc) : null;
  const endDate = event.endUtc ? eventDayKey({ ...event, startUtc: event.endUtc }) : null;
  return {
    startDate: eventDayKey(event),
    startTime: allDay ? null : start.ok ? start.value.clock : null,
    endDate,
    endTime: allDay ? null : end?.ok ? end.value.clock : null,
    isAllDay: allDay, wrongDateFlag: event.dateFlaggedWrong,
  };
}

type EventOverlay = Pick<ExtractedEvent,
  'rowKey' | 'partitionKey' | 'startUtc' | 'endUtc' | 'isAllDay' | 'updatedAt'
  | 'reviewWarnings' | 'dateReviewReason' | 'dateBasis' | 'sourceDateText' | 'sourceTimeText'>;

/** Apply only server-returned temporal/provenance fields. Enrichment and user flags stay intact. */
export function dateProofEventOverlay(previous: ExtractedEvent, response: DateProofChooseResponse): EventOverlay {
  const event = response.event;
  if (!response.receiptId?.trim() || !event || event.rowKey !== previous.rowKey
    || !event.partitionKey || !event.startUtc || !event.updatedAt || !response.proof
    || !Array.isArray(event.reviewWarnings)) {
    throw new Error('The server did not return a durable save receipt and this occurrence. Refresh stored evidence.');
  }
  readDateProofResponse(response.proof);
  return {
    rowKey: event.rowKey, partitionKey: event.partitionKey,
    startUtc: event.startUtc, endUtc: event.endUtc, isAllDay: event.isAllDay,
    updatedAt: event.updatedAt, reviewWarnings: event.reviewWarnings,
    dateReviewReason: event.dateReviewReason, dateBasis: event.dateBasis,
    sourceDateText: event.sourceDateText, sourceTimeText: event.sourceTimeText,
  };
}

/** A later independent edit/read wins; a stale in-flight list fetch cannot undo a save. */
export function overlayDateProofEvent(event: ExtractedEvent, overlay?: EventOverlay): ExtractedEvent {
  if (!overlay || event.rowKey !== overlay.rowKey || event.updatedAt > overlay.updatedAt) return event;
  return { ...event, ...overlay };
}

interface ReviewState {
  event: ExtractedEvent;
  proof: DateProofReadResponse | null;
  status: DateProofPanelStatus;
  message: string | null;
  job: DateProofVerifyResponse | null;
}

interface ReviewContext {
  open: (event: ExtractedEvent) => void;
  activeId: string | null;
  panelId: string;
  overlays: ReadonlyMap<string, EventOverlay>;
}
const Context = createContext<ReviewContext | null>(null);
const occurrenceKey = (event: Pick<ExtractedEvent, 'partitionKey' | 'rowKey'>) =>
  `${event.partitionKey}|${event.rowKey}`;

export function useDateProofReview() {
  const context = useContext(Context);
  if (!context) throw new Error('DateProofReviewProvider is required.');
  return context;
}

/** Use before filtering/grouping, not only at the final formatted time string. */
export function useDateProofEvents(events: ExtractedEvent[]): ExtractedEvent[];
export function useDateProofEvents(events: ExtractedEvent[] | null): ExtractedEvent[] | null;
export function useDateProofEvents(events: ExtractedEvent[] | null): ExtractedEvent[] | null {
  const context = useContext(Context);
  return useMemo(() => events?.map((event) => overlayDateProofEvent(
    event, context?.overlays.get(occurrenceKey(event)),
  )) ?? null, [events, context?.overlays]);
}

export function useDateProofEvent(event: ExtractedEvent | null): ExtractedEvent | null {
  const context = useContext(Context);
  return event ? overlayDateProofEvent(event, context?.overlays.get(occurrenceKey(event))) : null;
}

/** Shared warning placement adapter. No effects, reads, hover work or verification. */
export function DateProofWarning({ event }: { event: ExtractedEvent }) {
  const { open, activeId, panelId } = useDateProofReview();
  return <EventTemporalWarning eventTitle={event.title} warnings={event.reviewWarnings}
    onOpen={() => open(event)} expanded={activeId === occurrenceKey(event)} panelId={panelId} />;
}

/** Stack a single warning under the time tile, inside the normal time column. */
export function DateProofSchedule({ event, children }: { event: ExtractedEvent; children: ReactNode }) {
  if (!event.reviewWarnings?.some((warning) => warning === 'check-date' || warning === 'time-unverified')) {
    return <>{children}</>;
  }
  return <div className="mh-event-row__schedule">
    {children}
    <DateProofWarning event={event} />
  </div>;
}

const FAILURE_MESSAGES = {
  load: "Couldn't load the source details.",
  // A lost response is not proof the save failed, so never claim that it did.
  save: "Couldn't confirm your choice was saved. Try again to check.",
  verify: "Couldn't start the recheck.",
};
/** Refusals a retry cannot fix, such as a choice that would move the event to another month. */
const NEEDS_MANUAL_EDIT = /"code":"(?:cross-partition-requires-receipt-safe-move|all-day-review-required|temporal-review-required|range-review-required)"/;

export function dateProofFailure(error: unknown, action: keyof typeof FAILURE_MESSAGES): { status: 'stale' | 'error'; message: string } {
  const status = (error as { status?: number } | null)?.status;
  if (error instanceof Error && NEEDS_MANUAL_EDIT.test(error.message)) return {
    status: 'error',
    message: "This option can't be saved from here. Use Fix date/time on the event instead.",
  };
  if (status === 409 || status === 412) return {
    status: 'stale',
    message: 'This event changed. Try again before choosing.',
  };
  return {
    status: 'error',
    message: isAuthPending(error) ? 'Your session expired. Reload the page to sign in.'
      : status === 403 ? "You don't have permission to do this."
        : FAILURE_MESSAGES[action],
  };
}

export function DateProofReviewProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const generatedId = useId();
  const panelId = `mh-date-proof-review-${generatedId}`;
  const [review, setReview] = useState<ReviewState | null>(null);
  const [overlays, setOverlays] = useState<ReadonlyMap<string, EventOverlay>>(() => new Map());
  const reviewRef = useRef(review);
  reviewRef.current = review;
  const generation = useRef(0);
  const busy = useRef(false);
  const opener = useRef<HTMLElement | null>(null);
  const previousLocation = useRef(location.key);
  useEffect(() => {
    if (previousLocation.current === location.key) return;
    previousLocation.current = location.key;
    ++generation.current;
    setReview(null);
  }, [location.key]);

  const load = useCallback(async (event: ExtractedEvent, token: number) => {
    try {
      const proof = readDateProofResponse(await api.get<DateProofReadResponse>(
        `${dateProofPath(event)}?partitionKey=${encodeURIComponent(event.partitionKey)}`,
      ));
      if (generation.current === token) setReview((current) => current && ({
        ...current, proof, status: 'ready', message: null,
      }));
    } catch (error) {
      if (generation.current === token) setReview((current) => current && ({ ...current, ...dateProofFailure(error, 'load') }));
    }
  }, []);

  const open = useCallback((event: ExtractedEvent) => {
    if (busy.current) return;
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const token = ++generation.current;
    setReview({ event, proof: null, status: 'loading', message: null, job: null });
    void load(event, token);
  }, [load]);
  const close = useCallback(() => {
    if (busy.current) return;
    ++generation.current; // Ignore late GETs, including a close/reopen of the same event.
    setReview(null);
  }, []);
  const refresh = useCallback(() => {
    const current = reviewRef.current;
    if (!current || busy.current) return;
    const token = ++generation.current;
    setReview({ ...current, status: 'loading', message: null });
    void load(current.event, token);
  }, [load]);
  const choose = useCallback(async (candidateId: string, fields: DateProofFields) => {
    const current = reviewRef.current;
    if (!current?.proof || current.status !== 'ready' || busy.current) return;
    busy.current = true; // Synchronous single-flight, before React can rerender.
    const token = generation.current;
    setReview({ ...current, status: 'saving', message: null });
    try {
      const body = dateProofChooseBody(current.event, current.proof, candidateId, fields);
      const response = await api.post<DateProofChooseResponse>(`${dateProofPath(current.event)}/choose`, body);
      const overlay = dateProofEventOverlay(current.event, response);
      setOverlays((previous) => new Map(previous)
        .set(occurrenceKey(current.event), overlay).set(occurrenceKey(overlay), overlay));
      // Keep MSAL's storage, tokens and all unrelated caches untouched.
      for (const prefix of ['events:', 'events-map:', 'calendar-posts:', 'admin-events:', 'jobs:']) {
        clearSwrCacheByPrefix(prefix);
      }
      if (generation.current === token) setReview({
        ...current, event: overlayDateProofEvent(current.event, overlay), proof: response.proof,
        status: 'ready', message: 'Saved for this occurrence.',
      });
    } catch (error) {
      if (generation.current === token) setReview({ ...current, ...dateProofFailure(error, 'save') });
    } finally {
      busy.current = false;
    }
  }, []);
  const verify = useCallback(async () => {
    const current = reviewRef.current;
    if (!current?.proof || current.status !== 'ready' || current.job || busy.current) return;
    busy.current = true;
    const token = generation.current;
    setReview({ ...current, status: 'saving', message: 'Starting the recheck…' });
    try {
      const job = await api.post<DateProofVerifyResponse>(`${dateProofPath(current.event)}/verify`, {
        partitionKey: current.event.partitionKey, expectedEtag: current.proof.expectedEtag,
      });
      if (!job?.jobId || !job.jobPk) throw new Error('No durable job identity was returned.');
      if (generation.current === token) setReview({ ...current, job, status: 'ready', message: 'Recheck started. Open this again later to see the result.' });
    } catch (error) {
      if (generation.current === token) setReview({ ...current, ...dateProofFailure(error, 'verify') });
    } finally {
      busy.current = false;
    }
  }, []);
  const context = useMemo(() => ({
    open, activeId: review ? occurrenceKey(review.event) : null, panelId, overlays,
  }), [open, review?.event.rowKey, review?.event.partitionKey, panelId, overlays]);

  return <Context.Provider value={context}>
    {children}
    {review && createPortal(
      <ProofDialog review={review} panelId={panelId} opener={opener.current}
        onClose={close} onRefresh={refresh} onChoose={choose} onVerify={verify} />,
      document.body,
    )}
  </Context.Provider>;
}

/** Native dialog owns top-layer backdrop, focus trapping and background inertness. */
function ProofDialog({ review, panelId, opener, onClose, onRefresh, onChoose, onVerify }: {
  review: ReviewState; panelId: string; opener: HTMLElement | null;
  onClose: () => void; onRefresh: () => void;
  onChoose: (id: string, fields: DateProofFields) => void; onVerify: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const saving = review.status === 'saving';
  useEffect(() => {
    const element = dialog.current!;
    const overflow = document.body.style.overflow;
    const background = opener?.closest('.mh-event-row, .mh-event-card, .mh-modal-dialog, .mh-cal-pill, .mh-cal-week-event, .mh-map-popup-event');
    element.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      element.close();
      // An underlying custom modal can unmount on browser navigation while
      // this top-layer dialog is open. Do not restore its abandoned body lock.
      document.body.style.overflow = overflow === 'hidden' && !document.querySelector('.mh-modal-backdrop')
        ? '' : overflow;
      // Restore after React has removed the dialog. Native close/unmount focus
      // restoration must not overwrite our fallback when the row moved groups.
      queueMicrotask(() => {
        if (document.querySelector('dialog[open]')) return;
        const fallback = opener?.isConnected ? opener
          : background?.isConnected ? background.querySelector<HTMLElement>('button, a[href]')
            : document.querySelector<HTMLElement>('main') ?? document.getElementById('root');
        if (!fallback) return;
        if (fallback.matches('button, a[href], input, select, textarea, [tabindex]')) {
          fallback.focus({ preventScroll: true });
        } else {
          fallback.setAttribute('tabindex', '-1');
          fallback.focus({ preventScroll: true });
          fallback.addEventListener('blur', () => fallback.removeAttribute('tabindex'), { once: true });
        }
      });
    };
  }, [opener]);
  return <dialog ref={dialog} className="p-0 rounded border shadow"
    aria-labelledby={`${panelId}-title`} aria-describedby={`${panelId}-saved`}
    style={{ width: 'min(36rem, calc(100% - 2rem))', maxHeight: 'calc(100dvh - 2rem)',
      background: 'var(--mh-surface)', color: 'var(--mh-text)', borderColor: 'var(--mh-border)' }}
    onCancel={(event) => { event.preventDefault(); if (!saving) onClose(); }}
    onKeyDown={(event) => {
      // Do not let Escape/Enter close or navigate an underlying event detail/row.
      event.stopPropagation();
      if (event.key === 'Escape') { event.preventDefault(); if (!saving) onClose(); }
    }}>
    <DateProofPanel eventTitle={review.event.title} warnings={review.event.reviewWarnings}
      current={{ ...(review.proof?.current ?? currentDateProofValues(review.event)),
        wrongDateFlag: review.event.dateFlaggedWrong || review.proof?.current.wrongDateFlag }}
      detail={review.proof?.details ?? null} candidates={review.proof?.candidates ?? []}
      history={review.proof?.history ?? []} status={review.status}
      message={review.message} sourceUrl={review.event.rawUrl} id={panelId} titleId={`${panelId}-title`}
      onChoose={onChoose} onClose={onClose} onRefresh={onRefresh}
      footer={review.proof && <>
        {review.status === 'ready' && review.message && (
          <p className="mt-3 mb-0" role="status" aria-atomic="true">{review.message}</p>
        )}
        <div className="mh-date-proof__footer">
          <button type="button" className="btn btn-outline-secondary"
            disabled={review.status !== 'ready' || !!review.job}
            onClick={onVerify} aria-describedby={`${panelId}-verify-scope`}>
            Recheck source
          </button>
          {review.job
            ? <Link to="/admin/jobs" className="btn btn-outline-secondary">View in Jobs</Link>
            : <span id={`${panelId}-verify-scope`} className="mh-date-proof__muted">May use a paid source check.</span>}
        </div>
      </>} />
  </dialog>;
}
